import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { apiFetch } from '../lib/api';
import { createMutation } from '../lib/mutation';
import { useSessionMe } from '../lib/session';
import { ConfirmDialog } from './ConfirmDialog';
import { Panel } from './Panel';
import { Button } from './ui/Button';

// The WITHDRAW half of the separate cross-border consent (#5881, PIPL Art. 15:
// withdrawing must be as convenient as granting). <CrossBorderGate/> is where the
// consent is given; this is where it is taken back, on the page that holds the
// person's own settings (`routes/Notifications.tsx`).
//
// It reads the SAME query the app gate reads (`useSessionMe`, keyed on the
// credential), so the re-read after a withdrawal is exactly what flips the gate:
// the server caps the session again, `/v1/me` reports `missing`, and the console
// is replaced by the consent screen, from which the person can grant again.
//
// Shown only for a declared `CN` with the consent CURRENT (`missing: false`, the
// server's verdict). An absent field (older `/v1/me`, a PAT) paints nothing, as
// it never walls. The view carries the notice version, not a grant date: a
// current grant is for that version, so the version is what is stated.

export function CrossBorderConsentSection(props: {
  token: string | null;
  onNotice?: (msg: string) => void;
}): JSX.Element {
  const me = useSessionMe(props.token);
  const [asking, setAsking] = createSignal(false);

  const granted = (): string | undefined => {
    const view = me.data;
    const consent = view?.cross_border_consent;
    if (view?.user?.region !== 'CN' || consent == null || consent.missing) return undefined;
    return consent.current_version;
  };

  const withdraw = createMutation({
    call: (version: string) =>
      apiFetch('/v1/me/consents', props.token, {
        method: 'POST',
        body: JSON.stringify({ kind: 'cross_border_cn', decision: 'withdraw', version }),
      }),
    fallback: t('consent.cross_border_withdraw_error'),
    onError: (message) => props.onNotice?.(message),
    // Both outcomes: the server is the arbiter, and a refused withdrawal (a stale
    // tab, already withdrawn) converges on its truth too.
    onSettled: () => me.refetch(),
  });

  return (
    <Show when={granted()}>
      {(version) => (
        <Panel title={t('consent.cross_border_settings_title')}>
          <p class="card__desc">
            {t('consent.cross_border_settings_granted', { version: version() })}
          </p>
          <div>
            <Button variant="outline" disabled={withdraw.pending()} onClick={() => setAsking(true)}>
              {t('consent.cross_border_withdraw')}
            </Button>
          </div>
          <ConfirmDialog
            open={asking()}
            title={t('consent.cross_border_withdraw_title')}
            desc={t('consent.cross_border_withdraw_desc')}
            cancelLabel={t('consent.cross_border_withdraw_cancel')}
            actionLabel={t('consent.cross_border_withdraw_confirm')}
            destructive
            onCancel={() => setAsking(false)}
            onConfirm={() => {
              setAsking(false);
              void withdraw.run(version());
            }}
          />
        </Panel>
      )}
    </Show>
  );
}
